import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { EMAIL_COLUMNS, EMAIL_ACTIONS } from '../data/constants/communications.constants';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ApiCommunicationsService } from '../data/services/api/api-communications.service';
import { CommunicationService } from '../data/services/view/communication.service';

@Component({
  selector: 'app-communications-list',
  templateUrl: './communications-list.component.html',
  styleUrls: ['./communications-list.component.scss'],
  providers: [MessageService, ConfirmationService],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CommunicationsListComponent implements OnInit, OnDestroy {
  columns = EMAIL_COLUMNS;
  actions = EMAIL_ACTIONS;
  
  // Error handling
  hasError = false;
  errorMessage = '';
  
  // Loading & Empty states
  isLoading = false;
  emptyMessage = 'No se han enviado comunicaciones aún. Las comunicaciones se generan desde las campañas activas';
  
  // Filtros avanzados
  channelFilter: string[] = [];
  statusFilter: string[] = [];
  dateRangeFilter: Date[] = [];
  
  channelOptions = [
    { label: 'Email', value: 'email' },
    { label: 'SMS', value: 'sms' },
    { label: 'WhatsApp', value: 'whatsapp' },
    { label: 'Llamada', value: 'call' }
  ];
  
  statusOptions = [
    { label: 'Pendiente', value: 'PENDING' },
    { label: 'Enviado', value: 'SENT' },
    { label: 'Entregado', value: 'DELIVERED' },
    { label: 'Abierto', value: 'OPENED' },
    { label: 'Click', value: 'CLICKED' },
    { label: 'Rebotado', value: 'BOUNCED' },
    { label: 'Fallido', value: 'FAILED' }
  ];

  constructor(
    public apiCommunicationsService: ApiCommunicationsService,
    public communicationService: CommunicationService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    // No necesitamos auto-refresh para comunicaciones (datos históricos)
  }

  ngOnDestroy() {
    // Limpiar dialogs abiertos
    this.communicationService.showDetail = false;
    this.communicationService.showPreview = false;
    this.communicationService.showTracking = false;
  }

  /**
   * Transforma un email agregando campos computed
   */
  private transformEmail(email: any): any {
    return {
      ...email,
      // Campos de populate (extraer de relaciones si vienen populated)
      // CORRECCIÓN: Verificar múltiples fuentes para strategyName
      campaignStrategyName: 
        email.strategyId?.name ||                    // Si strategyId populated
        email.campaignId?.strategyId?.name ||        // Si campaignId populated con nested
        email.campaignStrategyName ||                // Si ya viene calculado
        'N/A',
      
      // CORRECCIÓN: leaseNumber puede no existir si leaseId es string
      leaseNumber: 
        email.leaseId?.leaseNumber ||                // Si leaseId populated
        email.leaseNumber ||                         // Si ya viene calculado
        email.leaseId ||                             // Último recurso: mostrar ObjectId
        'N/A',
      
      clientName: 
        email.clientId?.name ||                      // Si clientId populated
        email.clientName ||                          // Si ya viene calculado
        'N/A',
      
      recipientEmail: email.to || email.recipientEmail || 'N/A',
      recipientName: email.clientId?.name || email.recipientName || 'N/A',
      
      // Campos computed
      engagementDisplay: this.communicationService.getEngagementDisplay(email),
      channelType: 'email', // Por ahora solo email
      
      // CORRECCIÓN: Calcular de tracking events si existen
      openCount: this.calculateOpenCount(email),
      clickCount: this.calculateClickCount(email)
    };
  }

  /**
   * Calcula el número de aperturas del email
   */
  private calculateOpenCount(email: any): number {
    // Si tracking events existen, contar eventos OPENED
    if (email.trackingEvents?.length) {
      return email.trackingEvents.filter((e: any) => e.type === 'OPENED').length;
    }
    // Fallback simple: si openedAt existe, al menos hubo 1 apertura
    return email.openedAt ? 1 : 0;
  }

  /**
   * Calcula el número de clicks en enlaces del email
   */
  private calculateClickCount(email: any): number {
    // Si tracking events existen, contar eventos CLICKED
    if (email.trackingEvents?.length) {
      return email.trackingEvents.filter((e: any) => e.type === 'CLICKED').length;
    }
    // Fallback simple: si clickedAt existe, al menos hubo 1 click
    return email.clickedAt ? 1 : 0;
  }

  /**
   * Maneja las acciones de la tabla
   */
  handleTableAction(event: any) {
    const { action, data } = event;
    
    switch (action) {
      case 'VIEW':
        this.viewDetail(data._id);
        break;
      case 'PREVIEW':
        this.viewPreview(data._id);
        break;
      case 'TRACKING':
        this.viewTracking(data._id);
        break;
      case 'RESEND':
        this.resendEmail(data._id);
        break;
    }
  }

  /**
   * Ver detalle de email
   */
  viewDetail(id: string): void {
    this.apiCommunicationsService.getById(id).subscribe((response: any) => {
      if (!response.error) {
        this.communicationService.selectedEmail = this.transformEmail(response.data);
        this.communicationService.showDetail = true;
      } else {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: response.msg
        });
      }
    });
  }

  /**
   * Ver preview HTML del email
   */
  viewPreview(id: string) {
    this.apiCommunicationsService.getById(id).subscribe((response: any) => {
      if (!response.error) {
        const email = this.transformEmail(response.data);
        this.communicationService.selectedEmail = email;
        this.communicationService.previewHtml = email.bodyHtml || '';
        this.communicationService.showPreview = true;
      } else {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: response.msg
        });
      }
    });
  }

  /**
   * Ver tracking timeline
   */
  viewTracking(id: string) {
    this.apiCommunicationsService.getById(id).subscribe((response: any) => {
      if (!response.error) {
        const email = this.transformEmail(response.data);
        this.communicationService.selectedEmail = email;
        this.communicationService.trackingEvents = 
          this.communicationService.buildTrackingTimeline(email);
        this.communicationService.showTracking = true;
      } else {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: response.msg
        });
      }
    });
  }

  /**
   * Reenviar email
   */
  resendEmail(id: string) {
    this.confirmationService.confirm({
      message: '¿Reenviar este email? Se creará un nuevo envío.',
      header: 'Confirmar Reenvío',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.apiCommunicationsService.resend(id).subscribe((r) => {
          if (r.error) {
            return this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: r.msg
            });
          }
          
          this.messageService.add({
            severity: 'success',
            summary: 'Éxito',
            detail: 'Email reenviado correctamente'
          });
          
          // Refrescar tabla
          this.refreshData();
        });
      }
    });
  }

  /**
   * Aplicar filtros a la tabla
   */
  applyFilters() {
    // Los filtros se aplican automáticamente a través del MainTableComponent
    // Este método puede usarse para lógica adicional si es necesario
  }

  /**
   * Limpiar todos los filtros
   */
  clearFilters() {
    this.channelFilter = [];
    this.statusFilter = [];
    this.dateRangeFilter = [];
  }

  /**
   * Refrescar datos de la tabla
   */
  private refreshData() {
    // Trigger para refrescar MainTableComponent
    // Implementar según necesidad
  }

  /**
   * Maneja errores de forma centralizada
   */
  handleError(error: any): void {
    this.hasError = true;
    this.errorMessage = error.message || 'Ocurrió un error al cargar los datos';
    console.error('Error in CommunicationsListComponent:', error);
    
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: this.errorMessage,
      life: 5000
    });
    this.cdr.markForCheck();
  }

  /**
   * Reintenta la operación después de un error
   */
  retry(): void {
    this.hasError = false;
    this.errorMessage = '';
    window.location.reload();
  }
}
